import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Input from "./components/Input";
import Select from "./components/Select";
import SoilLayerTable from "./SoilLayerTable";
import Collapsible from "react-collapsible";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spacer from "./components/Spacer";
import { LOCAL_PREFIX } from "../../utils/constants";
import ConfirmModal from "../common/ConfirmModal";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";
import { levelToColor, levelToIconName } from "./components/ProblemIndicator";

const TEXTURE_OPTIONS = ["Sand", "Sandy Loam", "Loam", "Clay Loam", "Clay"];

const LAB_OPTIONS = [
  "AgLab Express in South Dakota",
  "Ag Source",
  "American Ag Lab out of McCook",
  "Deveron in Clear Lake, Iowa",
  "Midwest Labs",
  "Trace Genomics",
  "UNL Soil Lab",
  "Ward Labs",
];

function getConfirmMessage(toDelete) {
  if (toDelete.isSaved) {
    return `This will remove "${toDelete.name}". After saving you will not be able to recover this change. If unsaved, you can reload the page to recover this application.`;
  }

  return `This will permanently remove "${toDelete.name}". This cannot be undone.`;
}

export default function SoilReportItem({
  index,
  sample,
  locations,
  formProblems,
  onRemove,
  checkDirty,
  setFormProblems,
}) {
  const tableRef = useRef(undefined);

  const [name, setName] = useState(sample.name ?? "");
  const [sampleDate, setSampleDate] = useState(sample.sampleDate ?? "");
  const [lab, setLab] = useState(sample.lab ?? "");
  const [labDate, setLabDate] = useState(sample.labDate ?? "");
  const [note, setNote] = useState(sample.note ?? "");
  const [acres, setAcres] = useState(sample.acres ?? "");
  const [texture, setTexture] = useState(sample.texture ?? "");

  const location = useMemo(() => {
    const l = locations?.find((loc) => loc._id === sample.locationId);

    if (!l) {
      return { name: 'Unknown Location', latLng: 'Unknown' };
    }

    return {
      name: l.SoilLocationID,
      latLng: `${l.geometry.coordinates[0]} | ${l.geometry.coordinates[1]}`,
    };
  }, [locations, sample.locationId]);

  const [toDelete, setToDelete] = useState(undefined);

  const prefix = `soil-${sample.id}`;

  const checkDirtyInternal = useCallback(() => {
    checkDirty({
      id: sample.id, locationId: sample.locationId, name, sampleDate,
      lab, labDate, note, acres, texture, ...tableRef.current,
    });
  }, [name, sampleDate, lab, labDate, note, acres, texture, checkDirty]);

  useEffect(() => checkDirtyInternal(), [checkDirtyInternal]);

  const setAndClearProblem = useCallback((setter, value, field) => {
    const i = formProblems.findIndex((p) => p.field === field);
    if (i >= 0) {
      formProblems.splice(i, 1);
    }

    setter(value);
  }, [formProblems]);

  const problemLevel = getHighestLevelWithPrefix(formProblems, prefix, true);

  return (<>
    <Collapsible
      className="w-full bg-zinc-300 p-3 rounded-3xl"
      openedClassName="w-full bg-zinc-300 p-3 rounded-3xl"
      contentOuterClassName="w-full flex flex-col"
      contentInnerClassName="flex-1 ml-9"
      triggerClassName="Collapsible__trigger group"
      triggerOpenedClassName="Collapsible__trigger group"
      trigger={(
        <div className="flex gap-2.5 items-center">
          <FontAwesomeIcon
            icon="chevron-circle-down"
            size="xl"
            className="group-[.is-closed]:-rotate-90 transition-all text-dark-blue"
          />
          <h2
            className="text-lg font-bold"
            title={location.latLng}
          >
            Sample {index + 1} - {location.name}

            {sample.failedToSave && (
              <FontAwesomeIcon
                icon="exclamation-circle"
                className="ml-1 text-red-500"
                data-tooltip-id="unsaved-sample-tooltip"
              />
            )}
            {!sample.failedToSave && problemLevel !== "" && (
              <FontAwesomeIcon
                icon={levelToIconName(problemLevel)}
                className={`ml-1 ${levelToColor(problemLevel)}`}
                data-tooltip-id="invalid-sample-tooltip"
              />
            )}
          </h2>
          <Spacer />
          <button
            title="Remove this sample"
            // Create some hit slop for the button.
            className="h-7 w-7 p-1 group-[.is-closed]:hidden rounded-lg text-white text-lg mr-2"
            onClickCapture={(e) => {
              e.stopPropagation();
              setToDelete({
                name: `Sample ${index + 1} - ${location.name}`,
                confirm: onRemove,
                isSaved: !sample.id.startsWith(LOCAL_PREFIX),
              });
            }}
          >
            <div
              className="bg-red-500 h-5 w-5 rounded-full flex items-center justify-center"
            >
              -
            </div>
          </button>
        </div>
      )}

      transitionTime={150}
      open
    >
      <div className="flex gap-2">
        <Input
          label="Sample Name or ID"
          name={`${prefix}-name`}
          value={name}
          onChange={(v) => setAndClearProblem(setName, v, `${prefix}-name`)}
          formErrors={formProblems}
        />
        <Input
          label="Date Sample Taken"
          name={`${prefix}-sample-date`}
          value={sampleDate}
          onChange={(value) => setAndClearProblem(setSampleDate, value, `${prefix}-sample-date`)}
          type="date"
          formErrors={formProblems}
        />
      </div>
      <div className="flex gap-2">
        <Select
          label="Lab Name"
          name={`${prefix}-lab`}
          value={lab}
          onChange={({ value }) => setAndClearProblem(setLab, value, `${prefix}-lab`)}
          options={LAB_OPTIONS.map((value) => ({ label: value, value }))}
          formErrors={formProblems}
        />
        <Input
          label="Date On Lab Report"
          name={`${prefix}-report-date`}
          value={labDate}
          onChange={(v) => setAndClearProblem(setLabDate, v, `${prefix}-report-date`)}
          type="date"
          formErrors={formProblems}
        />
      </div>
      <Input
        label="Sample Note"
        name={`${prefix}-note`}
        value={note}
        onChange={setNote}
        type="textarea"
        formErrors={formProblems}
      />
      <div className="flex gap-2">
        <Input
          label="Acres Represented"
          name={`${prefix}-acres`}
          value={acres}
          onChange={(v) => setAndClearProblem(setAcres, v, `${prefix}-acres`)}
          type="number"
          formErrors={formProblems}
        />
        <Select
          label="Soil Texture"
          name={`${prefix}-texture`}
          value={texture}
          onChange={({ value }) => setAndClearProblem(setTexture, value, `${prefix}-texture`)}
          options={TEXTURE_OPTIONS.map((value) => ({ label: value, value }))}
          formErrors={formProblems}
        />
      </div>

      <SoilLayerTable
        ref={tableRef}
        sample={sample}
        checkDirty={checkDirtyInternal}
        formProblems={formProblems}
        setAndClearProblem={setAndClearProblem}
        setFormProblems={setFormProblems}
      />
    </Collapsible>
    <ConfirmModal
      isOpen={!!toDelete}
      title="Confirm Delete Soil Sample"
      message={toDelete ? getConfirmMessage(toDelete) : ""}
      confirmButtonLabel="Yes, Delete"
      toggle={() => setToDelete(undefined)}
      onConfirm={() => {
        toDelete.confirm();
        setToDelete(undefined);
      }}
      secondaryButtonText="Cancel"
      warning
    />
  </>);
}
